import {
  WalletConnectModal,
  type WalletConnectModalConfig,
} from "@walletconnect/modal";
import type { CoreTypes, SessionTypes } from "@walletconnect/types";
import UniversalProvider from "@walletconnect/universal-provider";
import { getSdkError } from "@walletconnect/utils";
import {
  XRPL_METHOD,
  XrplCAIP2ChainId,
  XrplConnector,
  XrplEvents,
  XrplNetwork,
  XrplTransaction,
  XrplTxStatus,
} from "../../../core/types/xrpl";
import { isXrplAddressValid } from "../../../services/internal/xrplService";

interface XrplWalletConnectProps {
  projectId: string;
  metadata?: CoreTypes.Metadata;
  recommendedWalletId?: string;
}

const chains = [XrplCAIP2ChainId.MAINNET, XrplCAIP2ChainId.TESTNET];

export class XrplWalletConnect implements XrplConnector {
  provider: UniversalProvider | null = null;

  session: SessionTypes.Struct | null = null;

  modal: WalletConnectModal | null = null;

  projectId: XrplWalletConnectProps["projectId"];

  metadata: XrplWalletConnectProps["metadata"];

  recommendedWalletId: XrplWalletConnectProps["recommendedWalletId"];

  readonly listeners: {
    [E in keyof XrplEvents]?: Array<(event: XrplEvents[E]) => void>;
  } = {};

  constructor({
    projectId,
    metadata,
    recommendedWalletId,
  }: XrplWalletConnectProps) {
    this.metadata = metadata;
    this.projectId = projectId;
    this.recommendedWalletId = recommendedWalletId;
  }

  private validateInitialized(): asserts this is this & {
    provider: UniversalProvider;
    modal: WalletConnectModal;
  } {
    if (!this.provider) {
      throw new Error("Provider is not initialized");
    }
  }

  /**
   * Sets up providers and event listeners, must be called before connecting
   *
   * - initializes WalletConnect UniversalProvider
   * - initializes WalletConnectModal
   * - attaches event listeners
   */
  async initialize() {
    if (this.provider) return;

    this.provider = await UniversalProvider.init({
      projectId: this.projectId,
      // logger: "debug",
      metadata: this.metadata,
      // We need to specify a custom storage prefix to avoid conflicts with Wagmi's walletConnect instance
      // https://docs.reown.com/walletkit/web/usage#core-instance-sharing
      customStoragePrefix: "squid-xrpl",
    });

    this.provider.client.on("session_delete", () => {
      this.emit("disconnect", undefined);
    });

    const options: Partial<WalletConnectModalConfig> = this.recommendedWalletId
      ? {
          explorerRecommendedWalletIds: [this.recommendedWalletId],
          explorerExcludedWalletIds: "ALL",
        }
      : {};

    this.modal = new WalletConnectModal({
      projectId: this.projectId,
      chains,
      ...options,
    });
  }

  /**
   * Initializes WalletConnect and silently connects to the last session if it exists
   */
  async autoConnect() {
    await this.initialize();
    this.validateInitialized();

    const sessions = this.provider.client.session.getAll();
    if (sessions.length === 0) {
      this.emit("disconnect", undefined);
      return null;
    }

    const lastKeyIndex = sessions.length - 1;
    const lastSession = sessions[lastKeyIndex];

    return this.onSessionConnected(lastSession);
  }

  /**
   * Initializes WalletConnect and prompts the user to connect to a new session
   */
  async connect() {
    await this.initialize();
    this.validateInitialized();

    const { uri, approval } = await this.provider.client.connect({
      requiredNamespaces: {
        xrpl: {
          chains: [XrplCAIP2ChainId.MAINNET],
          methods: [XRPL_METHOD.SIGN_TRANSACTION],
          events: ["accountsChanged"],
        },
      },
    });

    let modalClosed: (reason?: any) => void = () => {};
    const modalClosedPromise = new Promise<never>((_, reject) => {
      modalClosed = reject;
    });

    let unsubscribe = () => {};

    if (uri) {
      this.modal.openModal({ uri });

      unsubscribe = this.modal.subscribeModal((state) => {
        if (state.open === false) {
          // propagate the error by rejecting the promise
          // to avoid connect() from running forever
          modalClosed(new Error("User closed the modal"));
        }
      });
    }

    try {
      const session = await Promise.race([approval(), modalClosedPromise]);
      const address = await this.onSessionConnected(session);
      return address;
    } finally {
      unsubscribe();
      this.modal.closeModal();
    }
  }

  /**
   * Disconnects from the current session
   */
  async disconnect() {
    if (!this.session) throw new Error("Session is not connected");

    this.validateInitialized();

    await this.provider.client.disconnect({
      topic: this.session.topic,
      reason: getSdkError("USER_DISCONNECTED"),
    });
  }

  /**
   * Prompts the user to sign and submit a transaction
   */
  async signAndSubmit({
    network,
    tx,
  }: {
    network: XrplNetwork;
    tx: XrplTransaction;
  }) {
    if (!this.session) throw new Error("Session is not connected");

    this.validateInitialized();

    const result = await this.provider.client.request<{
      tx_json: Record<string, any>;
    }>({
      chainId: this.getWcChainId(network),
      topic: this.session.topic,
      request: {
        method: XRPL_METHOD.SIGN_TRANSACTION,
        params: {
          tx_json: tx,
          autofill: true,
          submit: true,
        },
      },
    });

    const { hash } = result.tx_json;

    if (!hash) {
      throw new Error("Invalid transaction hash");
    }

    return {
      hash,
      // Wallet side handles submitting and validating the transaction
      status: XrplTxStatus.SUCCESS,
    };
  }

  /**
   * Maps the given network to the WalletConnect chain ID format
   */
  private getWcChainId(network: XrplNetwork): string {
    switch (network) {
      case XrplNetwork.MAINNET:
        return XrplCAIP2ChainId.MAINNET;
      case XrplNetwork.TESTNET:
        return XrplCAIP2ChainId.TESTNET;
      default:
        throw new Error(`Network ${network} is not supported`);
    }
  }

  /**
   * Stores the provided session and returns the user's address
   */
  async onSessionConnected(session: SessionTypes.Struct): Promise<string> {
    this.session = session;
    const allNamespaceAccounts = Object.values(session.namespaces).flatMap(
      (namespace) => namespace.accounts
    );

    const [firstAccount] = allNamespaceAccounts;

    const address = firstAccount?.split(":")?.at(-1);

    if (address && isXrplAddressValid(address)) {
      return address;
    }

    throw new Error("No address found in session");
  }

  on<E extends keyof XrplEvents>(
    event: E,
    listener: (event: XrplEvents[E]) => void
  ) {
    if (this.listeners[event]) this.listeners[event]?.push(listener);
    else this.listeners[event] = [listener];
    return () => this.off(event, listener);
  }

  private emit<E extends keyof XrplEvents>(
    event: E,
    args: XrplEvents[E]
  ): void {
    this.listeners[event]?.forEach((listener) => listener.apply(null, [args]));
  }

  private off<E extends keyof XrplEvents>(
    event: E,
    listener: (event: XrplEvents[E]) => void
  ): void {
    this.listeners[event] = this.listeners[event]?.filter(
      (existingListener) => listener !== existingListener
    );
  }
}
